/**
 * The active clinic, as the transport layer sees it — plus the access-change signal.
 *
 * Two module-level values, and both exist for the same reason `paywallSignal.js`
 * does: the fetch wrapper is not inside React, so it cannot read context, and
 * threading the clinic through every one of the ~40 hooks that call `bffQuery`
 * would mean every future hook could forget to.
 *
 * WHY A MODULE SINGLETON IS SAFE HERE, when §A1 spent a page arguing against
 * storing the tenant: this is not storage, it is a mirror. The AUTHORITY is the
 * `/c/:ref/…` route; `ClinicContextProvider` writes this on every route change and
 * nothing else may. Two tabs each have their own module instance, so they hold two
 * different clinics with no coordination — which is the whole point of putting the
 * tenant in the URL (v1's E3 needed a rule to achieve this; the route gets it free).
 *
 * `localStorage` appears exactly once, and only to answer "where should a bare `/`
 * go?" — a preference, never an authority.
 */

const LAST_CLINIC_KEY = 'clinic.lastId.v1';

// ── The active clinic ref (slug or id), mirrored from the route ──────────────

let activeRef = '';

export function getActiveClinicRef() {
  return activeRef;
}

/** Called by ClinicContextProvider on every route change. Nothing else may call it. */
export function setActiveClinicRef(ref) {
  activeRef = ref || '';
}

// ── The remembered clinic — a hint for a bare `/`, nothing more ──────────────

export function readLastClinic() {
  try {
    return window.localStorage.getItem(LAST_CLINIC_KEY) || '';
  } catch {
    // Private mode, blocked site data, or a browser that throws on access. A
    // person with no remembered clinic simply lands on their most recent one, so
    // there is nothing to recover from.
    return '';
  }
}

export function rememberLastClinic(ref) {
  try {
    if (ref) window.localStorage.setItem(LAST_CLINIC_KEY, ref);
  } catch { /* preference only */ }
}

/**
 * Deliberately NOT cleared when a clinic becomes unreachable.
 *
 * A stored ref that no longer resolves is ignored on the next boot (E1) at no
 * cost, whereas clearing it on a failed request would silently demote a returning
 * owner to "pick a clinic" after a single network blip (E2).
 */

// ── The access-change signal ─────────────────────────────────────────────────

let currentVersion = '';
const versionListeners = new Set();

/** Called by the fetch layer whenever a response carried `myAccess.version`. */
export function reportAccessVersion(version) {
  if (!version) return;
  if (!currentVersion) { currentVersion = version; return; }
  if (version === currentVersion) return;
  currentVersion = version;
  versionListeners.forEach((fn) => { try { fn(version); } catch { /* listener's problem */ } });
}

/** Reset the baseline — on a switch, the new clinic's stamp is not a change. */
export function resetAccessVersion(version = '') {
  currentVersion = version;
}

export function onAccessVersionChange(fn) {
  versionListeners.add(fn);
  return () => versionListeners.delete(fn);
}

// ── The unsaved-work guard ───────────────────────────────────────────────────
//
// Switching clinics throws the whole routed tree away (that is what makes "no
// stale row survives a switch" structural rather than a checklist). Throwing away
// DATA is the point; throwing away someone's half-typed patient without asking is
// not, and the two are the same keystroke.
//
// So a screen with unsaved work registers a guard, and the switcher asks before it
// navigates. Deliberately the same shape as the doctor app's
// `window.__parsina_visit_guard`: one registration point, so a screen cannot half
// participate, and the switcher never has to know what kind of work is open.
//
// A guard that throws is IGNORED rather than allowed to block the switch — a
// broken confirm must not be able to trap someone in a clinic.

let switchGuard = null;

/**
 * Register the current screen's unsaved-work check.
 *
 * @param {null | (proceed: () => void) => void} guard
 *   Called with a continuation. Call it to allow the switch; do nothing to cancel.
 * @returns {() => void} unregister — return it straight from an effect.
 */
export function registerSwitchGuard(guard) {
  switchGuard = typeof guard === 'function' ? guard : null;
  return () => { if (switchGuard === guard) switchGuard = null; };
}

/** Run `proceed` once the open screen has had its say. */
export function guardedSwitch(proceed) {
  if (!switchGuard) { proceed(); return; }
  try {
    switchGuard(proceed);
  } catch (err) {
    // A guard that throws must not be able to trap someone in a clinic.
    console.error('[clinicSignal] switch guard failed, allowing the switch:', err);
    proceed();
  }
}

/** Test seam. */
export function clearSwitchGuard() { switchGuard = null; }
